export interface Workspace {
  id: string;
  account: string;
  app: string;
  name: string;
  isolationMode: string;
  schemaName: string | null;
  isDefault: boolean;
  archived: boolean;
  /** The workspace this one sits under; absent for a top-level workspace. */
  parentId?: string | null;
  memberCount?: number;
  /** Set by the workspace list (GET fetch) for the caller: 1 = top-level. */
  depth?: number;
  /** What the caller may do with this workspace (GET fetch only; the backend enforces the same). */
  canManage?: boolean;
  canArchive?: boolean;
  canAddChild?: boolean;
  canMove?: boolean;
  createdOn?: string;
  createdBy?: string;
  updatedOn?: string;
  updatedBy?: string;
}

/** Levels a workspace tree may have (a top-level workspace plus two below it). Mirrors WorkspaceTree.MAX_DEPTH. */
export const MAX_WORKSPACE_DEPTH = 3;

/** One row of the enrollment search: a user's membership in one workspace, with its place in the tree. */
export interface WorkspaceEnrollment {
  membershipId: string;
  userEmail: string;
  workspaceId: string;
  workspaceName: string;
  /** Names from the top-level workspace down to this one. */
  path: string[];
  archived: boolean;
  roleId: number;
  roleName?: string;
}

/** ApiResponse envelope returned by add/update/archive endpoints. */
export interface WorkspaceApiResponse {
  code: number;
  message: string; // for `add`, this is the new workspace id
}

export interface WorkspaceRole {
  id: number;
  account: string | null;
  name: string;
  preset: boolean;
  permissionsJson: string; // stringified JSON array — what add/update send
  permissions?: string[]; // derived array returned in responses
  createdOn?: string;
  createdBy?: string;
}

export interface WorkspaceMembership {
  id: string;
  workspaceId: string;
  account: string;
  app: string;
  userEmail: string;
  roleId: number;
  createdOn?: string;
  createdBy?: string;
}

/** Per-workspace branding (mirrors the backend CustomBranding fields). */
export interface WorkspaceBranding {
  name?: string;
  appUrl?: string;
  senderEmail?: string;
  senderDisplayName?: string;
  logoUrl?: string;
  primaryColor?: string;
  footerHtml?: string;
  buttonTextColor?: string;
  enabled: boolean;
}

/**
 * A workspace's envelope quota for the current billing period (mirrors the backend WorkspaceQuota).
 * `quota` is absent when no quota is set. `eligible` is false on plans without quotas
 * (free, trial, lifetime). `periodEnd` is the period's last day.
 */
export interface WorkspaceQuota {
  workspaceId: string;
  quota?: number;
  periodStart?: string;
  periodEnd?: string;
  used: number;
  remaining?: number;
  percentUsed?: number;
  eligible: boolean;
  planLimit?: number;
  warning?: string;
}

/** The workspace-permission catalog (mirrors the backend WorkspacePermission constants). */
export const WORKSPACE_PERMISSIONS: { key: string; label: string }[] = [
  { key: "document.view.any", label: "View all documents" },
  { key: "document.view.own", label: "View own documents" },
  { key: "document.create", label: "Create documents" },
  { key: "document.send", label: "Send documents" },
  { key: "document.void", label: "Void documents" },
  { key: "document.download", label: "Download documents" },
  { key: "template.manage", label: "Manage templates" },
  { key: "member.manage", label: "Manage members" },
  { key: "workspace.settings", label: "Workspace settings" },
  { key: "role.manage", label: "Manage roles" },
  { key: "audit.view", label: "View audit trail" },
  { key: "report.view", label: "View reports" },
];
